import React, {useEffect, useState} from 'react';
import {makeApiCall} from "../../../Api/makeApiCall";
import {getStringParamOrDefault} from "../../../utils/urlBuilder";
import NameValueLabels from "../../Components/NameValueLabels";
import ReportLink from "./ReportLink";
import Page from "../../Components/Page";
import {REPORT_TYPES} from "../../Configurations/ReportTypes";
import {ManifestDropdown} from "./manifestDropdown";
import {SourceFilesReportActions} from "./SourceFilesReportActions";
import {getEdpAirflowInfo} from "../../../utils/airflow";
import {buildDagRunUrl} from "../../../utils/airflowLinks";
import {CONFIG_TYPES} from "../../Configurations/FileTypes";
import ReactTable from "../../Components/ReactTable";
import {BulkLoadArchiveButton, bulkLoadOptionsEnum} from "../../Retailer/BulkLoadArchiveButton";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import _ from "lodash";

export const getMultiSourceReportColumns = (archiveLocation, ftp) => {
    return [
        {
            header: 'Partition Path',
            accessorKey: 'partitionPath',
            className: 'non-numeric-field',
            minSize: 60,
            enableColumnFilter: false
        },
        {
            header: 'Normalized Record Count',
            accessorKey: 'normalizedRecordCount',
            className: 'numeric-field',
            maxSize: 50,
            enableColumnFilter: false
        },
        {
            header: 'Source Files',
            accessorKey: 'sourceFiles',
            cell: ({getValue, row}) => <SourceFilesReportActions
                archiveLocation={archiveLocation}
                sourceFiles={Object.entries(getValue()).map(([key, v]) => ({[key]: v})).sort(
                    (a, b) => {
                        if (Object.keys(a)[0] === row.original.usedFile) {
                            return -1;
                        }
                        if (Object.keys(b)[0] === row.original.usedFile) {
                            return 1;
                        }
                        return Object.keys(a)[0].localeCompare(Object.keys(b)[0]);
                    }
                )}
                displayFileName
                usedFile={row.original.usedFile}
                ftp={ftp}
            />,
            className: 'non-numeric-field',
            enableColumnFilter: false
        }
    ]
};

export const getNormalizedReportsColumns = (archiveLocation, ftp, runId, data) => {

    function constructNormalizedRecordReportUrl(fileName) {
        return `https://storage.cloud.google.com/${process.env.REACT_APP_GCS_TMP_BASE_URL}${runId}/report/${encodeURIComponent('p_data=normalized')}/normalized_record_report/${encodeURIComponent(fileName)}.json`;
    }

    return [
        {
            header: 'Actions',
            id: 'actions',
            accessorKey: 'fileName',
            cell: ({getValue}) => <SourceFilesReportActions
                archiveLocation={archiveLocation}
                sourceFiles={[{[getValue()]: ''}]}
                ftp={ftp}
            />,
            className: 'non-numeric-field normalized-report-actions',
            size: 100,
            enableColumnFilter: false
        },
        {
            header: 'File Name',
            accessorKey: 'fileName',
            className: 'non-numeric-field'
        },
        {
            header: 'Failed Record Count',
            accessorKey: 'failedRecordCount',
            cell: ({getValue, row}) => (
                <div className={'normalized-failed-record-count-value'}>
                    {(getValue() ?
                        <a href={constructNormalizedRecordReportUrl(row.original.fileName)}>
                            {getValue()}</a> : getValue())
                    }
                </div>
            ),
            className: 'numeric-field',
            size: 40
        },
        {
            header: 'Total Record Count',
            accessorKey: 'totalRecordCount',
            className: 'numeric-field',
            size: 40
        },
        {
            header: 'Percent Records Failed',
            accessorKey: 'percentRecordsFailed',
            className: 'numeric-field',
            size: 40
        },
        {
            header: 'Ingest Mappings',
            accessorKey: 'ingestMappings',
            className: 'non-numeric-field',
            cell: ({getValue}) => {
                const mappings = getValue().split("\n").filter(x => x.trim() !== '');
                return !_.isEmpty(mappings) ? <ul className={"no-bullet-list"} >{mappings.map(mapping => <li>{mapping}</li>)}</ul> : <span/>;
            },
        },
        {
            header: 'Manifest',
            accessorKey: 'manifest',
            cell: ({row}) => <ManifestDropdown ftp={ftp} configName={row.original.manifest}
                                               manifestFileName={row.original.manifestFileName}
                                               ingestFileName={row.original.fileName}
                                               data={data}/>,
            className: 'non-numeric-field table-dropdown'
        }
    ]
}

const NormalizedReport = () => {

    const path = getStringParamOrDefault('path');
    const runId = getStringParamOrDefault('runId');
    const runDate = getStringParamOrDefault('runDate');
    const dagName = getStringParamOrDefault('dagName');
    const reportType = getStringParamOrDefault('reportType');
    const archiveLocation = getStringParamOrDefault('archiveLocation');
    const ftp = getStringParamOrDefault('ftp');
    const retailerId = getStringParamOrDefault('retailerId');

    const [data, setData] = useState(undefined);
    const [airflowInfo, setAirflowInfo] = useState(undefined);
    const [ftpConfigData, setFtpConfigData] = useState({});
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        setLoading(true);
        const params = {ftp: ftp, configType: "file_type"}
        const pathEncoded = encodeURIComponent(path);

        Promise.all([
            getEdpAirflowInfo(),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/ftpConfig/`, params),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/getGcsFileByPath/?path=${pathEncoded}&file_type=text`),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/manifest/`, {manifestFileName: ftp})
        ]).then(([
                edpAirflowInfoResults,
                ftpConfigDataResults,
                reportDataResults,
                manifestDataResults
            ]) => {
            const reportData = [];

            reportDataResults.trim().split("\n").forEach(x => {
                const record = JSON.parse(x);
                if (record.ingestMappings) {
                    record.ingestMappings = prettyFormatObject(record.ingestMappings)
                }
                record.manifest = Object.keys(manifestDataResults.configFiles).includes(record.fileName) ?
                    manifestDataResults.configFiles[record.fileName] : undefined;
                reportData.push(record);
            })

            setData(reportData);
            setAirflowInfo(edpAirflowInfoResults);
            setFtpConfigData(ftpConfigDataResults);

        }).finally(() => setLoading(false));
    }, []);


    function prettyFormatObject(record) {
        let formattedObject = "";
        Object.keys(record).forEach(key => {
            formattedObject += key + " (" + record[key] + ")\n"
        })
        return formattedObject;
    }

    function getAirFlowUrl() {
        const airFlowUrl = buildDagRunUrl({
            airflowUrl: airflowInfo?.uri,
            airflowVersion: airflowInfo?.airflowVersion,
            dagId: dagName,
            runId,
            executionDate: runDate,
            includeBaseDate: false
        });
        return <a target='_blank' id={'run-id-tag'} rel="noopener noreferrer" href={airFlowUrl}>{runId}</a>;
    }

    const labelValuePairs = [
        {label: "Dag Run:", value: getAirFlowUrl()},
        {
            label: "Report:", value: <ReportLink
                displayValue={reportType}
                runId={encodeURIComponent(runId)}
                fileType={CONFIG_TYPES.SALES}
                gcsSuffix={`/${reportType}`}
            />
        },
        {label: "FTP:", value: ftp},
        {label: "", value: <BulkLoadArchiveButton retailerId={retailerId} fileType={bulkLoadOptionsEnum.sales}/>}
    ];

    const isNormalizedFileReport = reportType === REPORT_TYPES.NORMALIZED_FILE;
    const pageName = isNormalizedFileReport ? "File Report" : "Multiple Source Report";
    const columns = isNormalizedFileReport ? getNormalizedReportsColumns(archiveLocation, ftp, runId, ftpConfigData)
        : getMultiSourceReportColumns(archiveLocation, ftp);

    return (
        <Page name={pageName}>
            <LoadingWrapper loading={loading}>
                <div className={"page-header"}>
                    <NameValueLabels labelValuePairs={labelValuePairs}/>
                </div>
                <ReactTable
                    data={data}
                    columns={columns}
                    filterable={true}
                />
            </LoadingWrapper>
        </Page>
    );
}

export default NormalizedReport;
